// UploadMediaModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import axios from 'axios';
import DropzoneArea from './DropzoneArea';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import MirakelParticles from './MirakelParticles';
import config from '../config';

interface UploadMediaModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UploadData) => void;
  uploadedImages: string[];
  handlePreview: (data: UploadData) => void;
  productNumber: string;
  availableColors: string[];
  initialColorCode: string;
  attribute1Number: string; // Ny prop
  productId?: number; // Ny prop för produkt-ID
  onFetchFromSupplier?: (
    attribute1Number: string,
    searchTerm?: string,
  ) => Promise<SupplierFetchResult>; // returnerar leverantörsbilder + sökmetadata
  supplierName?: string; // Ny prop för leverantörsnamn
  onError?: (message: string) => void; // Ny prop för error-hantering
}

interface SupplierImage {
  type: string;
  url?: string;
  fileId?: string;
  fileName?: string;
}

interface SupplierFetchResult {
  images: SupplierImage[];
  suggestedSearchTerm?: string | null;
  fallbackSearchTerm?: string | null;
  attemptedEans?: string[];
}

/** Kontainer CDN tillåter sällan browser-fetch (CORS); samma mönster som backend allowlist. */
function supplierImageNeedsBackendProxy(url: string): boolean {
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h.includes('cdn.kontainer.com');
  } catch {
    return false;
  }
}

function fileNameFromSupplierUrl(
  url: string | undefined,
  index: number,
  mime?: string,
  fallbackName?: string,
): string {
  let candidate = '';
  try {
    if (url) {
      candidate = decodeURIComponent(
        new URL(url).pathname.split('/').pop() || '',
      );
    }
  } catch {
    // ignore
  }

  candidate = candidate || fallbackName?.trim() || '';
  if (candidate) {
    if (mime === 'image/png' && /\.(?:tif|tiff)$/i.test(candidate)) {
      return candidate.replace(/\.(?:tif|tiff)$/i, '.png');
    }
    if (mime === 'image/jpeg' && /\.(?:tif|tiff)$/i.test(candidate)) {
      return candidate.replace(/\.(?:tif|tiff)$/i, '.jpg');
    }
    return candidate;
  }
  return `supplier_image_${index + 1}`;
}

const SUPPLIER_IMAGE_DOWNLOAD_TIMEOUT_MS = 120_000;

async function supplierImageToBlob(image: SupplierImage): Promise<{
  blob: Blob;
  mime: string;
}> {
  const url = image.url?.trim() || '';
  if (image.fileId || supplierImageNeedsBackendProxy(url)) {
    const resp = await axios.post(
      `${config.API_URL}/products/supplier-cdn-image`,
      image.fileId ? { fileId: image.fileId } : { url },
      {
        responseType: 'blob',
        timeout: SUPPLIER_IMAGE_DOWNLOAD_TIMEOUT_MS,
      },
    );
    const mime =
      String(resp.headers['content-type'] || '')
        .split(';')[0]
        .trim() ||
      (resp.data as Blob).type ||
      'application/octet-stream';
    return { blob: resp.data as Blob, mime };
  }

  if (!url) {
    throw new Error('Leverantörsbild saknar URL och fileId');
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    SUPPLIER_IMAGE_DOWNLOAD_TIMEOUT_MS,
  );
  try {
    const r = await fetch(url, { signal: controller.signal });
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}`);
    }
    const blob = await r.blob();
    const mime =
      r.headers.get('content-type')?.split(';')[0]?.trim() ||
      blob.type ||
      'application/octet-stream';
    return { blob, mime };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

interface UploadData {
  articleNumber: string;
  colorCode: string;
  attribute1Number: string;
  images: File[];
  transparentIndices: number[];
}

const UploadMediaModal: React.FC<UploadMediaModalProps> = ({
  open,
  onClose,
  onSubmit,
  uploadedImages,
  handlePreview,
  productNumber,
  availableColors,
  initialColorCode,
  attribute1Number, // Ny prop
  onFetchFromSupplier, // Ny prop
  supplierName, // Ny prop
  onError, // Ny prop
}) => {
  const [selectedColor, setSelectedColor] = useState<string>(
    initialColorCode || '',
  );
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transparentIndices, setTransparentIndices] = useState<number[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingFromSupplier, setIsFetchingFromSupplier] = useState(false);
  const [manualSearchOpen, setManualSearchOpen] = useState(false);
  const [manualSearchTerm, setManualSearchTerm] = useState('');
  const [manualSearchMessage, setManualSearchMessage] = useState('');

  // Funktion för att återställa all state
  const resetModalState = () => {
    setSelectedColor(initialColorCode || '');
    setFiles([]);
    setIsProcessing(false);
    setTransparentIndices([]);
    setSelectedImage(null);
    setIsSaving(false);
    setIsFetchingFromSupplier(false);
    setManualSearchOpen(false);
    setManualSearchTerm('');
    setManualSearchMessage('');
  };

  // Återställ state när modalen stängs
  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open, initialColorCode]);

  // Uppdatera selectedColor när initialColorCode ändras
  useEffect(() => {
    setSelectedColor(initialColorCode || '');
  }, [initialColorCode]);

  const onDrop = (acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.filter((file) =>
      file.type.startsWith('image/'),
    );

    if (imageFiles.length < acceptedFiles.length) {
      alert('Endast bildfiler är tillåtna (PNG, JPG, JPEG, GIF, WEBP)');
    }

    setFiles((prevFiles) => [...prevFiles, ...imageFiles]);
  };

  const handlePreviewClick = async () => {
    if (!selectedColor || files.length === 0) {
      alert('Vänligen välj en färg och lägg till bilder.');
      return;
    }

    setIsProcessing(true);

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    formData.append('articleNumber', productNumber);
    formData.append('colorCode', selectedColor);
    formData.append('attribute1Number', attribute1Number);
    formData.append('transparentIndices', JSON.stringify(transparentIndices));

    await handlePreview({
      articleNumber: productNumber,
      colorCode: selectedColor,
      attribute1Number: attribute1Number,
      images: files,
      transparentIndices: transparentIndices,
    });

    setIsProcessing(false);
    setFiles([]);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSubmit({
        articleNumber: productNumber,
        colorCode: selectedColor,
        attribute1Number: attribute1Number,
        images: files,
        transparentIndices: transparentIndices,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
  };

  const handleCloseImageViewer = () => {
    setSelectedImage(null);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    // Uppdatera transparentIndices för att ta bort index som är större än det borttagna
    setTransparentIndices((prevIndices) =>
      prevIndices
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i)),
    );
  };

  const addSupplierImagesToFiles = async (images: SupplierImage[]) => {
    const filePromises = images.map(async (image: SupplierImage, index) => {
      try {
        const { blob, mime } = await supplierImageToBlob(image);
        const fileName = fileNameFromSupplierUrl(
          image.url,
          index + 1,
          mime,
          image.fileName,
        );
        const file = new File([blob], fileName, { type: mime });
        // Visa varje bild direkt när den är klar; vänta inte på den
        // långsammaste TIFF-nedladdningen innan något syns i modalen.
        setFiles((prevFiles) => [...prevFiles, file]);
        return file;
      } catch (error) {
        console.error('Error converting supplier image to file:', error);
        return null;
      }
    });

    const newFiles = (await Promise.all(filePromises)).filter(
      (file): file is File => file !== null,
    );
    return newFiles.length;
  };

  const handleFetchFromSupplier = async (searchTerm?: string) => {
    if (!onFetchFromSupplier) return;

    setIsFetchingFromSupplier(true);
    try {
      const result = await onFetchFromSupplier(attribute1Number, searchTerm);
      const images = result.images || [];

      if (images.length > 0) {
        const addedCount = await addSupplierImagesToFiles(images);

        if (addedCount === 0) {
          const message =
            'Bilder hittades hos leverantören men kunde inte laddas ner från CDN.';
          if (onError) {
            onError(message);
          } else {
            alert(message);
          }
          return;
        }

        if (addedCount < images.length) {
          const failedCount = images.length - addedCount;
          const message = `${addedCount} av ${images.length} leverantörsbilder lades till. ${failedCount} kunde inte laddas ner.`;
          if (onError) {
            onError(message);
          }
        }

        setManualSearchOpen(false);
        setManualSearchMessage('');
        return;
      }

      const suggested =
        result.suggestedSearchTerm ||
        result.fallbackSearchTerm ||
        searchTerm ||
        '';
      if (suggested) {
        setManualSearchTerm(suggested);
        setManualSearchMessage(
          result.attemptedEans?.length
            ? `Inga bilder hittades via EAN (${result.attemptedEans.join(', ')}) eller autosökning.`
            : 'Inga bilder hittades via autosökning.',
        );
        setManualSearchOpen(true);
      } else if (onError) {
        onError('Inga bilder hittades hos leverantören');
      } else {
        alert('Inga bilder hittades hos leverantören');
      }
    } catch (error) {
      console.error('Error fetching from supplier:', error);
      const errorMessage =
        'Ett fel inträffade vid hämtning av bilder från leverantör';
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsFetchingFromSupplier(false);
    }
  };

  const handleManualSupplierSearch = async () => {
    const term = manualSearchTerm.trim();
    if (!term) return;
    await handleFetchFromSupplier(term);
  };

  const canFetchFromSupplier =
    Boolean(onFetchFromSupplier) &&
    ['Bestseller', 'Sports Group Denmark', 'Sports Group'].includes(
      supplierName || '',
    );

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <div
          className="modal-content"
          style={{
            padding: '2rem',
            backgroundColor: '#FFFFFF',
            margin: '50px auto',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 18px 60px rgba(15, 23, 42, 0.18)',
            color: '#0F172A',
          }}
        >
          {isSaving && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(248, 250, 252, 0.78)',
                backdropFilter: 'blur(8px)',
                zIndex: 1,
                borderRadius: '8px',
              }}
            >
              <CircularProgress color="primary" />
            </div>
          )}

          <Box
            sx={{
              position: 'relative',
              mb: 3,
              mx: -1,
              px: 1,
              py: 0.5,
              overflow: 'hidden',
              borderRadius: 1,
              '& > :not(.mirakel-particles)': {
                position: 'relative',
                zIndex: 1,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -32,
                left: -32,
                right: -32,
                height: '4px',
                background:
                  'linear-gradient(90deg, #2563EB 0%, #06B6D4 52%, #F97316 100%)',
              },
            }}
          >
            <MirakelParticles variant="corner" density="low" opacity={0.28} />
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                marginBottom: '1rem',
                color: 'text.primary',
              }}
            >
              Ladda upp bilder
            </Typography>

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                color: 'text.secondary',
                marginBottom: '1.5rem',
              }}
            >
              Artikelnummer: {productNumber}
            </Typography>
          </Box>

          <Autocomplete
            options={availableColors}
            value={selectedColor}
            onChange={(event, newValue) => {
              setSelectedColor(newValue || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Färg"
                variant="outlined"
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          <DropzoneArea
            onDrop={onDrop}
            files={files}
            setFiles={setFiles}
            transparentIndices={transparentIndices}
            setTransparentIndices={setTransparentIndices}
            onRemoveFile={handleRemoveFile}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
            }}
          >
            {/* Knapp för att hämta från leverantör */}
            {canFetchFromSupplier && (
              <Button
                onClick={() => handleFetchFromSupplier()}
                variant="outlined"
                disabled={isFetchingFromSupplier}
                startIcon={
                  isFetchingFromSupplier ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                sx={{
                  '&:disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.38)',
                  },
                }}
              >
                {isFetchingFromSupplier ? 'Hämtar...' : 'Hämta från leverantör'}
              </Button>
            )}

            {/* Knapp för förhandsgranskning */}
            <Button
              onClick={handlePreviewClick}
              variant="outlined"
              disabled={isProcessing}
            >
              {isProcessing ? 'Bearbetar...' : 'Förhandsgranska'}
            </Button>
          </div>

          {uploadedImages.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  marginBottom: '1rem',
                  fontWeight: 600,
                }}
              >
                Bearbetade bilder:
              </Typography>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '1rem',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {uploadedImages.map((url, index) => {
                  const fileName = url.split('/').pop() || url;
                  const fullUrl = `https://mirakel-api.enghssport.com/processed/${fileName}`;
                  return (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        aspectRatio: '3/4',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition:
                          'border-color 160ms ease, box-shadow 160ms ease',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#F8FAFC',
                        '&:hover': {
                          borderColor: '#CBD5E1',
                          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
                        },
                      }}
                    >
                      <img
                        src={fullUrl}
                        alt={`Bild ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                        onClick={() => handleImageClick(fullUrl)}
                      />
                    </Box>
                  );
                })}
              </div>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '2rem',
              gap: '1rem',
            }}
          >
            <Button onClick={onClose} variant="outlined">
              Avbryt
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={uploadedImages.length === 0 && files.length === 0}
            >
              Spara
            </Button>
          </div>
        </div>
      </Modal>

      <Dialog
        open={manualSearchOpen}
        onClose={() => setManualSearchOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sök manuellt i Kontainer
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {manualSearchMessage ||
              'Inga bilder hittades automatiskt. Justera sökningen och försök igen.'}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Sökord"
            value={manualSearchTerm}
            onChange={(event) => setManualSearchTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isFetchingFromSupplier) {
                void handleManualSupplierSearch();
              }
            }}
            helperText="Exempel: E202560_1001. Sökningen används i stället för EAN."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManualSearchOpen(false)} variant="outlined">
            Avbryt
          </Button>
          <Button
            onClick={handleManualSupplierSearch}
            variant="contained"
            disabled={isFetchingFromSupplier || !manualSearchTerm.trim()}
            startIcon={
              isFetchingFromSupplier ? <CircularProgress size={16} /> : null
            }
          >
            {isFetchingFromSupplier ? 'Söker...' : 'Sök och hämta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal för förstorad bild */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseImageViewer}
        aria-labelledby="image-preview-modal"
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#FFFFFF',
            padding: '2rem',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 18px 60px rgba(15, 23, 42, 0.18)',
          }}
        >
          <img
            src={selectedImage || ''}
            alt="Förstorad bild"
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(90vh - 120px)',
              objectFit: 'contain',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
            }}
          />
          <Button
            onClick={handleCloseImageViewer}
            variant="outlined"
            sx={{
              marginTop: '1.5rem',
            }}
          >
            Stäng
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default UploadMediaModal;
